import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  process.env.VITE_API_URL ||
  'http://localhost:3002/api'
).trim();

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

let accessToken: string | null = null;
let refreshPromise: Promise<AuthResponse> | null = null;
let authFailureHandler: (() => void) | null = null;
let pendingRequests = 0;
const loadingListeners = new Set<(isLoading: boolean) => void>();

type AuthResponse = {
  token: string;
  user?: unknown;
};

type RetryableConfig = InternalAxiosRequestConfig & {
  _authRetry?: boolean;
  _globalLoadingTracked?: boolean;
  _skipGlobalLoading?: boolean;
};

const notifyLoadingListeners = () => {
  const isLoading = pendingRequests > 0;
  loadingListeners.forEach((listener) => listener(isLoading));
};

const beginTrackedRequest = (config: RetryableConfig) => {
  if (config._skipGlobalLoading || config._globalLoadingTracked) return;
  config._globalLoadingTracked = true;
  pendingRequests += 1;
  notifyLoadingListeners();
};

const finishTrackedRequest = (config?: RetryableConfig) => {
  if (!config?._globalLoadingTracked) return;
  config._globalLoadingTracked = false;
  pendingRequests = Math.max(0, pendingRequests - 1);
  notifyLoadingListeners();
};

export const subscribeToApiLoading = (listener: (isLoading: boolean) => void) => {
  loadingListeners.add(listener);
  listener(pendingRequests > 0);
  return () => {
    loadingListeners.delete(listener);
  };
};

export const setJwt = (token: string | null) => {
  accessToken = token;
  if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  else delete api.defaults.headers.common['Authorization'];
};

export const setAuthFailureHandler = (handler: (() => void) | null) => {
  authFailureHandler = handler;
};

export const getAccessTokenRefreshDelay = (token: string): number | null => {
  try {
    const encodedPayload = token.split('.')[1];
    if (!encodedPayload) return null;

    const base64Payload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = base64Payload.padEnd(Math.ceil(base64Payload.length / 4) * 4, '=');
    const payload = JSON.parse(window.atob(paddedPayload)) as { exp?: unknown };
    if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) return null;

    // Refresh one minute before expiry so normal work is uninterrupted.
    return Math.max(0, (payload.exp * 1000) - Date.now() - 60_000);
  } catch {
    return null;
  }
};

const wait = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

api.interceptors.request.use((config: RetryableConfig) => {
  const headers = config.headers instanceof AxiosHeaders ? config.headers : AxiosHeaders.from(config.headers);
  config.headers = headers;

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  } else if (config.headers) {
    headers.delete('Authorization');
  }

  beginTrackedRequest(config);
  return config;
});

api.interceptors.response.use(
  (response) => {
    finishTrackedRequest(response.config as RetryableConfig);
    return response;
  },
  (error: AxiosError) => {
    finishTrackedRequest(error.config as RetryableConfig | undefined);
    return Promise.reject(error);
  },
);

const isRefreshExcludedEndpoint = (url?: string) => {
  const path = String(url || '').split('?')[0];
  return path.endsWith('/auth/login')
    || path.endsWith('/auth/refresh')
    || path.endsWith('/auth/logout');
};

export const refreshAccessToken = async (): Promise<AuthResponse> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const retryDelays = [250, 500, 1000, 3500];
      for (let attempt = 0; ; attempt += 1) {
        try {
          const response = await api.post<AuthResponse>('/auth/refresh', {}, {
            headers: { 'X-CSRF-Protection': '1' },
          });
          if (!response.data?.token) throw new Error('Refresh response did not include an access token');
          setJwt(response.data.token);
          return response.data;
        } catch (error) {
          const shouldRetry = axios.isAxiosError(error)
            && error.response?.status === 409
            && error.response?.data?.code === 'REFRESH_RETRY'
            && attempt < retryDelays.length;
          if (!shouldRetry) throw error;
          await wait(retryDelays[attempt]);
        }
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined;
    if (
      error.response?.status !== 401
      || !original
      || original._authRetry
      || isRefreshExcludedEndpoint(original.url)
    ) {
      return Promise.reject(error);
    }

    original._authRetry = true;
    try {
      const refreshed = await refreshAccessToken();
      original.headers = AxiosHeaders.from(original.headers);
      original.headers.set('Authorization', `Bearer ${refreshed.token}`);
      return api(original);
    } catch (refreshError) {
      setJwt(null);
      authFailureHandler?.();
      return Promise.reject(refreshError);
    }
  },
);

export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

export default api;
